// ── BCHNostr — Podcast discovery ──────────────────────────────────
//
// Podcasts as a category inside the existing discovery surface: reached
// from Explore and from the Info/Community nav, with no new sidebar
// section of its own.
//
// Five ways in — New, Latest Episodes, Popular, Most Tipped, Most
// Followed — plus search across show name, host, episode title, and
// category. Promoted shows (bought through the existing boost auction)
// are surfaced first and labelled as promoted.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Mic2, Search, X, Sparkles, Clock, Flame, Coins, Users, Rocket,
  RefreshCw, Calendar, Play,
} from 'lucide-react';
import {
  usePodcastDirectory, usePodcastFollowerCounts, usePodcastTipTotals,
  usePodcastBoosts, usePodcastFeed,
} from '../hooks/usePodcast';
import {
  PODCAST_CATEGORIES, podcastCategoryLabel, podcastPath, episodePath,
  getCachedFeed, refreshFeed, isCacheStale,
  formatEpisodeDuration, formatEpisodeDate,
  type PodcastShow,
} from '../lib/podcast';
import { satsToUsdStr } from '../lib/boost';
import { useBCHPrice } from '../hooks/useBCHPrice';
import { useNostrProfile } from '../hooks/useNostrQuery';
import { useAppContext } from '../hooks/useAppContext';
import { OPERATOR_PUBKEY } from '../lib/platform';
import { BCHLogo } from '../components/Layout';
import BCHTipButton from '../components/BCHTipButton';
import PodcastSetup from '../components/PodcastSetup';

type SortKey = 'new' | 'episodes' | 'popular' | 'tipped' | 'followed';

const SORTS: Array<{ id: SortKey; label: string; icon: React.ElementType }> = [
  { id: 'new',      label: 'New',            icon: Sparkles },
  { id: 'episodes', label: 'Latest Episodes', icon: Clock },
  { id: 'popular',  label: 'Popular',        icon: Flame },
  { id: 'tipped',   label: 'Most Tipped',    icon: Coins },
  { id: 'followed', label: 'Most Followed',  icon: Users },
];

// ── Show card ─────────────────────────────────────────────────────

function ShowCard({
  show, followers, tippedSats, promoted, bchPrice,
}: {
  show: PodcastShow;
  followers: number;
  tippedSats: number;
  promoted?: boolean;
  bchPrice: number | null;
}) {
  const { profile } = useNostrProfile(show.pubkey);
  return (
    <Link
      to={podcastPath(show.pubkey)}
      className="flex items-start gap-3 px-4 py-3 border-b border-border hover:bg-muted/40 transition-colors group"
    >
      {show.image ? (
        <img
          src={show.image}
          alt=""
          loading="lazy"
          className="w-16 h-16 rounded-xl object-cover border border-border shrink-0"
        />
      ) : (
        <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <Mic2 className="w-6 h-6 text-primary/50" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <h3 className="text-sm font-bold truncate group-hover:text-primary transition-colors">
            {show.name}
          </h3>
          {promoted && (
            <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 text-[9px] font-bold uppercase tracking-wider">
              <Rocket className="w-2 h-2" /> Promoted
            </span>
          )}
        </div>
        {show.description && (
          <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{show.description}</p>
        )}
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 mt-1 text-[10px] text-muted-foreground">
          <span className="px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-semibold">
            {podcastCategoryLabel(show.category)}
          </span>
          {show.hosts.length > 0 && <span className="truncate max-w-[9rem]">{show.hosts.join(', ')}</span>}
          {followers > 0 && (
            <span className="flex items-center gap-1"><Users className="w-2.5 h-2.5" />{followers}</span>
          )}
          {tippedSats > 0 && (
            <span className="flex items-center gap-1 text-primary font-medium">
              <BCHLogo size={9} />
              {satsToUsdStr(tippedSats, bchPrice) || `${tippedSats.toLocaleString()} sats`}
            </span>
          )}
        </div>
      </div>
      <div
        className="no-navigate shrink-0 self-center"
        onClick={e => { e.preventDefault(); e.stopPropagation(); }}
      >
        <BCHTipButton
          recipientPubkey={show.pubkey}
          recipientProfile={profile}
          dropdownAlign="right"
        />
      </div>
    </Link>
  );
}

// ── Latest episode strip ──────────────────────────────────────────
//
// Shows one show's newest episode. Cache-first: it renders from whatever
// is already stored and only refreshes a genuinely stale feed, so opening
// this page never triggers N live feed fetches.

function LatestEpisodeRow({ show }: { show: PodcastShow }) {
  const { episodes } = usePodcastFeed(show.feedUrl);
  const episode = episodes[0];
  if (!episode) return null;

  const art = episode.image || show.image;
  const date = formatEpisodeDate(episode.publishedAt);
  const duration = formatEpisodeDuration(episode.duration);

  return (
    <Link
      to={episodePath(show.pubkey, episode.guid)}
      className="flex items-start gap-3 px-4 py-3 border-b border-border hover:bg-muted/40 transition-colors group"
    >
      {art ? (
        <img src={art} alt="" loading="lazy" className="w-14 h-14 rounded-lg object-cover border border-border shrink-0" />
      ) : (
        <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          <Mic2 className="w-5 h-5 text-primary/50" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-semibold text-primary truncate">{show.name}</p>
        <p className="text-sm font-semibold leading-snug line-clamp-2 group-hover:text-primary transition-colors">
          {episode.title}
        </p>
        <div className="flex items-center gap-3 mt-0.5 text-[10px] text-muted-foreground">
          {date && <span className="flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />{date}</span>}
          {duration && <span className="flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{duration}</span>}
        </div>
      </div>
      <div className="shrink-0 self-center p-2 rounded-full bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
        <Play className="w-3.5 h-3.5" />
      </div>
    </Link>
  );
}

// ── Page ──────────────────────────────────────────────────────────

export default function PodcastsPage() {
  const [sort, setSort] = useState<SortKey>('new');
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  const { price: bchPrice } = useBCHPrice();
  const { user } = useAppContext();
  const isOperator = !!user && user.pubkey === OPERATOR_PUBKEY;

  const { podcasts, loading, refetch } = usePodcastDirectory(category);
  const pubkeys = useMemo(() => podcasts.map(p => p.pubkey), [podcasts]);
  const followerCounts = usePodcastFollowerCounts(pubkeys);
  const tipTotals = usePodcastTipTotals(pubkeys);
  const { showBoosts } = usePodcastBoosts();

  // Warm the episode caches for the shows on screen, one stale feed at a
  // time. This is what makes search-by-episode-title and the Latest
  // Episodes tab work without a fetch storm on mount.
  useEffect(() => {
    const stale = podcasts
      .filter(p => isCacheStale(getCachedFeed(p.feedUrl)))
      .slice(0, 12);
    let cancelled = false;
    (async () => {
      for (const show of stale) {
        if (cancelled) return;
        await refreshFeed(show.feedUrl).catch(() => null);
      }
    })();
    return () => { cancelled = true; };
  }, [podcasts]);

  /**
   * Search across show name, hosts, category label, description, and
   * episode titles. Episode titles come from the local feed cache — the
   * search never triggers network requests of its own.
   */
  const matched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return podcasts;
    return podcasts.filter(show => {
      if (show.name.toLowerCase().includes(q)) return true;
      if (show.hosts.some(h => h.toLowerCase().includes(q))) return true;
      if (podcastCategoryLabel(show.category).toLowerCase().includes(q)) return true;
      if (show.description.toLowerCase().includes(q)) return true;
      const cached = getCachedFeed(show.feedUrl);
      return !!cached?.episodes.some(ep => ep.title.toLowerCase().includes(q));
    });
  }, [podcasts, query]);

  /** Newest episode timestamp per show, from cache. */
  const latestEpisodeAt = useMemo(() => {
    const map = new Map<string, number>();
    for (const show of podcasts) {
      const cached = getCachedFeed(show.feedUrl);
      map.set(show.pubkey, cached?.episodes[0]?.publishedAt || 0);
    }
    return map;
  }, [podcasts]);

  const sorted = useMemo(() => {
    const list = [...matched];
    switch (sort) {
      case 'episodes':
        return list.sort((a, b) =>
          (latestEpisodeAt.get(b.pubkey) || 0) - (latestEpisodeAt.get(a.pubkey) || 0));
      case 'tipped':
        return list.sort((a, b) => (tipTotals.get(b.pubkey) || 0) - (tipTotals.get(a.pubkey) || 0));
      case 'followed':
        return list.sort((a, b) =>
          (followerCounts.get(b.pubkey) || 0) - (followerCounts.get(a.pubkey) || 0));
      case 'popular':
        // "Popular" blends the two honest signals we actually have —
        // followers and tips — rather than inventing a play count.
        return list.sort((a, b) => {
          const score = (pk: string) =>
            (followerCounts.get(pk) || 0) * 10 + Math.log10(1 + (tipTotals.get(pk) || 0)) * 5;
          return score(b.pubkey) - score(a.pubkey);
        });
      case 'new':
      default:
        return list.sort((a, b) => b.created_at - a.created_at);
    }
  }, [matched, sort, latestEpisodeAt, tipTotals, followerCounts]);

  // Promoted shows float to the top — except when the user is explicitly
  // sorting or searching, where hijacking their intent would be hostile.
  const promoted = useMemo(() => {
    if (query.trim() || sort !== 'new') return [];
    return sorted
      .filter(s => showBoosts.has(s.pubkey))
      .sort((a, b) =>
        (showBoosts.get(b.pubkey)?.priceSats || 0) - (showBoosts.get(a.pubkey)?.priceSats || 0))
      .slice(0, 3);
  }, [sorted, showBoosts, query, sort]);

  const rest = useMemo(() => {
    const promotedKeys = new Set(promoted.map(p => p.pubkey));
    return sorted.filter(s => !promotedKeys.has(s.pubkey));
  }, [sorted, promoted]);

  return (
    <div className="pb-6">
      {/* ── Header ── */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Mic2 className="w-5 h-5 text-primary" /> BCH Podcasts
          </h1>
          <div className="flex items-center gap-1.5">
            {isOperator && (
              <Link
                to="/podcasts/seed"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted text-xs font-semibold hover:bg-muted/70"
                title="Seed the directory with existing shows (operator only)"
              >
                <Rocket className="w-3.5 h-3.5" /> Seed
              </Link>
            )}
            <button
              onClick={refetch}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-primary"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-4 pb-2.5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search podcasts, hosts, episodes…"
              className="w-full pl-9 pr-9 py-2 rounded-full bg-muted text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-full text-muted-foreground hover:text-foreground"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Sorts */}
        <div className="flex gap-1.5 px-4 pb-2 overflow-x-auto scrollbar-hide">
          {SORTS.map(s => (
            <button
              key={s.id}
              onClick={() => setSort(s.id)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                sort === s.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              <s.icon className="w-3 h-3" /> {s.label}
            </button>
          ))}
        </div>

        {/* Categories */}
        <div className="flex gap-1.5 px-4 pb-2.5 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => setCategory('all')}
            className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap border transition-colors ${
              category === 'all'
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:text-foreground'
            }`}
          >
            All
          </button>
          {PODCAST_CATEGORIES.map(c => (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap border transition-colors ${
                category === c.id
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:text-foreground'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Connect / manage your own podcast ── */}
      {/* Setup lives here, on the podcast surface, rather than in the
          profile form. A podcaster lands on /podcasts, pastes their feed,
          and their page exists. */}
      <PodcastSetup onDone={refetch} />

      {/* ── Body ── */}
      {loading && podcasts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Finding podcasts…</p>
        </div>
      ) : sorted.length === 0 ? (
        <div className="text-center py-16 px-6">
          <Mic2 className="w-12 h-12 mx-auto text-muted-foreground/25 mb-3" />
          <p className="text-muted-foreground text-sm">
            {query ? 'No podcasts match that search.' : 'No podcasts here yet.'}
          </p>
          {!query && (
            <p className="text-xs text-muted-foreground mt-1 max-w-xs mx-auto leading-relaxed">
              Have a show? Use <strong className="text-foreground">Connect your podcast</strong> above —
              paste your RSS feed and your page builds itself.
            </p>
          )}
        </div>
      ) : sort === 'episodes' ? (
        <>
          <p className="px-4 py-2 text-[10px] text-muted-foreground border-b border-border">
            The newest episode from each show, straight from their RSS feeds.
          </p>
          {sorted.slice(0, 40).map(show => (
            <LatestEpisodeRow key={show.pubkey} show={show} />
          ))}
        </>
      ) : (
        <>
          {promoted.length > 0 && (
            <>
              <p className="px-4 pt-3 pb-1 text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                Promoted
              </p>
              {promoted.map(show => (
                <ShowCard
                  key={show.pubkey}
                  show={show}
                  followers={followerCounts.get(show.pubkey) || 0}
                  tippedSats={tipTotals.get(show.pubkey) || 0}
                  promoted
                  bchPrice={bchPrice}
                />
              ))}
            </>
          )}
          {promoted.length > 0 && rest.length > 0 && (
            <p className="px-4 pt-3 pb-1 text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
              All podcasts
            </p>
          )}
          {rest.map(show => (
            <ShowCard
              key={show.pubkey}
              show={show}
              followers={followerCounts.get(show.pubkey) || 0}
              tippedSats={tipTotals.get(show.pubkey) || 0}
              bchPrice={bchPrice}
            />
          ))}
        </>
      )}

      {sorted.length > 0 && (
        <p className="px-4 pt-4 text-[10px] text-muted-foreground/70 leading-relaxed">
          Episodes and artwork come from each podcast's own RSS feed. BCHNostr doesn't host
          podcast audio — playback streams from the creator's host.
        </p>
      )}
    </div>
  );
}
